import React, { useEffect, useRef } from "react";
import _ from "lodash";

import { MatchScoreWidget } from "../../_partials/MatchScoreWidget/Index";
import CornerFrame from "../../../../shared/components/CornerFrame/Index.jsx";
import getContentById from "../../../../shared/utils/getContentById";
import { formatMinute, parseMinute, sortByTimeFirst } from "./helpers";

import "./style/style.scss";

// Builds the two shapes the markup switches between: a flat list (one row per
// goal) and one row per scorer with their minutes collapsed together.
const buildGoalLists = (entries) => {
  const flat =
    entries
      ?.filter((g) => g.min && g.name)
      .map((g) => ({
        playerName: g.name,
        timeMin: g.min,
        sortValue: parseMinute(g.min),
      }))
      .sort((a, b) => a.sortValue - b.sortValue) || [];

  const byPlayer = entries?.reduce((acc, goal) => {
    if (!goal.min || !goal.name) return acc;
    if (!acc[goal.name]) {
      acc[goal.name] = { playerName: goal.name, times: [] };
    }
    acc[goal.name].times.push(goal.min);
    return acc;
  }, {});

  return { flat, grouped: sortByTimeFirst(Object.values(byPlayer ?? {})) };
};

// Flag every goal that shares a row with the one after it, so the stylesheet
// can put a comma there — the last entry on each line stays comma-less.
const updateCommas = (container, show) => {
  if (!container) return;
  const items = Array.from(container.querySelectorAll(".goal-info"));
  // clear previous flags
  items.forEach((item) => item.classList.remove("show-comma"));
  if (!show) return;
  for (let i = 0; i < items.length - 1; i++) {
    // compare y position (top). Use getBoundingClientRect for sub-pixel accuracy.
    const topA = Math.round(items[i].getBoundingClientRect().top);
    const topB = Math.round(items[i + 1].getBoundingClientRect().top);
    if (topA === topB) {
      // same row -> show comma after this element
      items[i].classList.add("show-comma");
    }
  }
};

// The score/scorers block that sits on top of the background. Rendered at the
// template's real pixel size in every context — the image-editor preview just
// scales the whole thing down — so all of its sizing lives in
// ResultsNew/style/style.scss and nothing here needs a per-context variant.
const ResultOverlay = ({ data, manualEntries, time, isPenalty, showMultiline }) => {
  const { fields } = data?.data ?? {};
  const { matchDetails = {}, goalShots, lineUp, matchInfo } = data?.data?.dynamic ?? {};

  const homeTeam = data?.event?.home;
  const awayTeam = data?.event?.away;
  const matchStatus = matchDetails?.matchStatus;
  const competitionId = matchInfo?.competition?.id;

  const scores = matchDetails?.scores || {
    total: {
      home: null,
      away: null,
    },
  };
  const homeScore = getContentById("homeScore", fields);
  const awayScore = getContentById("awayScore", fields);
  const showScore = true;

  if (!_.isEmpty(homeScore)) {
    _.set(scores, "total.home", homeScore);
  }

  if (!_.isEmpty(awayScore)) {
    _.set(scores, "total.away", awayScore);
  }

  const awayContainerRef = useRef(null);
  const homeContainerRef = useRef(null);

  const hasManualScores = !!(manualEntries?.scores?.home || manualEntries?.scores?.away);

  let homeGoalGroup;
  let homeGoalsFlat;
  let awayGoalGroup;
  let awayGoalsFlat;

  if (hasManualScores) {
    ({ flat: homeGoalsFlat, grouped: homeGoalGroup } = buildGoalLists(
      manualEntries?.players?.home,
    ));
    ({ flat: awayGoalsFlat, grouped: awayGoalGroup } = buildGoalLists(
      manualEntries?.players?.away,
    ));
  } else {
    // The Opta-derived path below is disabled — scorers only come from the
    // manual entries above. Kept for reference until it is either restored
    // or dropped for good.
    //
    // const goals = goalShots?.filter(({ typeId }) => typeId === 16);
    // const homeKit = lineUp?.find(({ contestantId }) => contestantId === homeTeam.id)?.player;
    // const awayKit = lineUp?.find(({ contestantId }) => contestantId === awayTeam.id)?.player;
    //
    // homeGoalsFlat = goals
    //   .filter(g => (g.contestantId === homeTeam.id && !g.isOwnGoal) || (g.contestantId !== homeTeam.id && g.isOwnGoal))
    //   .map(g => {
    //     let p = homeKit.find(k => k.playerId === g.playerId);
    //     if (g.isOwnGoal) {
    //       p = awayKit.find(k => k.playerId === g.playerId);
    //     }
    //     return {
    //       playerId: g.playerId,
    //       playerName: (p?.shortLastName || g.playerName) + (g.isPenalty ? " (P)" : g.isOwnGoal ? " (OG)" : ""),
    //       timeMin: g.timeMin,
    //       sortValue: parseMinute(g.timeMin),
    //     };
    //   })
    //   .sort((a, b) => a.sortValue - b.sortValue);
    //
    // homeGoalGroup = Object.values(
    //   goals.reduce((acc, goal) => {
    //     const isHome = (goal.contestantId === homeTeam.id && !goal.isOwnGoal) || (goal.contestantId !== homeTeam.id && goal.isOwnGoal);
    //     if (isHome) {
    //       if (!acc[goal.playerId]) {
    //         let _player = homeKit.find((k) => k.playerId === goal.playerId);
    //         if (goal.isOwnGoal) {
    //           _player = awayKit.find((k) => k.playerId === goal.playerId);
    //         }
    //         acc[goal.playerId] = {
    //           playerName: _player?.shortLastName || goal.playerName,
    //           times: [],
    //           isPenalty: goal.isPenalty,
    //           isOwnGoal: goal.isOwnGoal
    //         };
    //       }
    //       acc[goal.playerId].times.push((goal.timeMin) + (goal.isPenalty ? " (P)" : goal.isOwnGoal ? " (OG)" : ""));
    //     }
    //     return acc;
    //   }, {}),
    // );
    //
    // awayGoalsFlat = goals
    //   .filter(g => (g.contestantId !== homeTeam.id && !g.isOwnGoal) || (g.contestantId === homeTeam.id && g.isOwnGoal))
    //   .map(g => {
    //     let p = awayKit.find(k => k.playerId === g.playerId);
    //     if (g.isOwnGoal) {
    //       p = homeKit.find(k => k.playerId === g.playerId);
    //     }
    //     return {
    //       playerId: g.playerId,
    //       playerName: (p?.shortLastName || g.playerName) + (g.isPenalty ? " (P)" : g.isOwnGoal ? " (OG)" : ""),
    //       timeMin: g.timeMin,
    //       sortValue: parseMinute(g.timeMin),
    //     };
    //   })
    //   .sort((a, b) => a.sortValue - b.sortValue);
    //
    // awayGoalGroup = Object.values(
    //   goals.reduce((acc, goal) => {
    //     const isHome = (goal.contestantId === homeTeam.id && !goal.isOwnGoal) || (goal.contestantId !== homeTeam.id && goal.isOwnGoal);
    //     if (!isHome) {
    //       if (!acc[goal.playerId]) {
    //         let _player = awayKit.find((k) => k.playerId === goal.playerId);
    //         if (goal.isOwnGoal) {
    //           _player = homeKit.find((k) => k.playerId === goal.playerId);
    //         }
    //         acc[goal.playerId] = {
    //           playerName: _player?.shortLastName || goal.playerName,
    //           times: [],
    //         };
    //       }
    //       acc[goal.playerId].times.push((goal.timeMin) + (goal.isPenalty ? " (P)" : goal.isOwnGoal ? " (OG)" : ""));
    //     }
    //     return acc;
    //   }, {}),
    // );
  }

  useEffect(() => {
    const refreshCommas = () => {
      updateCommas(homeContainerRef.current, showMultiline);
      updateCommas(awayContainerRef.current, showMultiline);
    };

    refreshCommas();
    // catch layout changes when fonts or images load
    window.addEventListener("load", refreshCommas);

    // If the goalGroups change, re-run (good to watch props; rerun effect)
    return () => {
      window.removeEventListener("load", refreshCommas);
    };
  }, [homeGoalGroup, awayGoalGroup, showMultiline]);

  return (
    <div className="wc-content" style={{ zIndex: 101 }}>
      <div className="content">
        <div className="match-length">{time}'</div>
        {/* <div className={`angled-border ${jersey === PresetColors.white ? "angle-border-white" : jersey === PresetColors.red ? "angle-border-red" : "angle-border-black"}`}>
          <img src={borderFrame} alt="border-frame" />
        </div> */}
        <CornerFrame>
          <div className="goal-block">
            <MatchScoreWidget
              homeTeam={homeTeam}
              awayTeam={awayTeam}
              scores={hasManualScores ? { total: { ...manualEntries.scores } } : scores}
              matchStatus={matchStatus}
              matchDetails={matchDetails}
              competitionId={competitionId}
              matchInfo={matchInfo}
              showScore={showScore}
            />
            <div className="goals">
              <div
                className={`home ${showMultiline && homeGoalGroup?.length > 2 ? "direction-row" : "direction-column"} home-goal-container`}
                ref={homeContainerRef}
              >
                {
                  !showMultiline ? homeGoalsFlat?.map((goal, index) => {
                    return (
                      <div className="goal-info" key={index}>
                        <span className="name">{goal.playerName}</span>
                        <span className="time">{formatMinute(goal.timeMin)}</span>
                      </div>
                    )
                  }) : homeGoalGroup?.map((goalGroup, index) => {
                    const formatted = goalGroup.times.map(formatMinute).join(', ');
                    return (
                      <div className="goal-info" key={index}>
                        <span className="name">{goalGroup.playerName}</span>
                        <span className="time">
                          {formatted}
                        </span>
                      </div>
                    );
                  })
                }
              </div>
              <div className="spaceText"></div>
              {
                isPenalty && <div className="penalty-container">
                  <div className="penalty-score">
                    ({manualEntries?.scores?.penHome}-{manualEntries?.scores?.penAway})
                  </div>
                  <div className="penalty-label">
                    PEN.
                  </div>
                </div>
              }
              <div
                className={`away ${showMultiline && awayGoalGroup?.length > 2 ? "direction-row" : "direction-column"} away-goal-container`}
                ref={awayContainerRef}
              >
                {
                  !showMultiline ? awayGoalsFlat?.map((goal, index) => {
                    return (
                      <div className="goal-info" key={index}>
                        <span className="time">{formatMinute(goal.timeMin)}</span>
                        <span className="name">{goal.playerName}</span>
                      </div>
                    )
                  }) : awayGoalGroup?.map((goalGroup, index) => {
                    const formatted = goalGroup.times.map(formatMinute).join(', ');
                    return (
                      <div className="goal-info" key={index}>
                        <span className="time">
                          {formatted}
                        </span>
                        <span className="name">{goalGroup.playerName}</span>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          </div>
        </CornerFrame>
      </div>
    </div>
  );
};

export default ResultOverlay;
